import { useCallback } from "react";
import * as React from "react";
import { Control, FieldValues, useController } from "react-hook-form";

import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

type PhoneFieldProps<T extends FieldValues = any> = {
  name: string;
  control: Control<T, object>;
  showIcon?: boolean;
  required?: boolean;
  label?: string;
  disabled?: boolean;
  sx?: SxProps;
};

export default function PhoneField({
  control,
  showIcon,
  name,
  label = "Phone Number",
  required = false,
  sx,
  disabled
}: PhoneFieldProps) {
  const validate = useCallback(
    (value: any) => {
      if (disabled) return true;
      if (required && !value) {
        return `${label} is required.`;
      }
      const phoneRegex = /^[[0-9]{3}?[-\s]?[0-9]{3}[-\s]?[0-9]{4}$/im;
      return !value || phoneRegex.test(value) || `${label} is not valid.`;
    },
    [required, disabled, label]
  );
  const { field, fieldState } = useController({
    name: name,
    control,
    rules: { validate }
  });
  const errorMessage = fieldState.error?.message;

  return (
    <Box display="flex" justifyContent="space-between" width="100%" sx={sx}>
      {showIcon && <LocalPhoneOutlinedIcon sx={{ mt: "30px", mr: 1.5 }} />}
      <TextField
        fullWidth
        label={label}
        required={required}
        type="text"
        disabled={disabled}
        error={Boolean(errorMessage)}
        helperText={errorMessage || ""}
        {...field}
        onChange={(event) => {
          field.onChange(event.target.value.replace(/\D/g, ""));
        }}
      />
    </Box>
  );
}
